define(() => {
    "use strict";

    /**
     * @type {*}
     * Map of all known enum types, used for reverse lookup (in for example polymorphic collections).
     */
    const UNIVERSE = {};

    /**
     * A simple and lightweight implementation of an enum. <p>
     *
     * Implementation note: is deliberately not dependent on external libraries such as underscore to avoid potential
     * issues with those in bleeding edge browsers like Google Canary.
     *
     * @author Gunni Rode / <a href="http://www.zmags.com">Zmags</a>
     */
    const Enum = {

        /**
         * Creator function that returns a new enum based on the supplied enum constants, either in form of
         * a plain old JS object (map) or an array. <p>
         *
         * The <tt>constants</tt> argument can take the following forms:
         *
         * <pre>
         *   {
         *      FOO: { .. }, // "FOO" key
         *      BAR: { .. }, // "BAR" key
         *      ..
         *   }
         *
         *   OR
         *
         *   [Foo, Bar, ..]  // using idField to deduce the key
         *
         * </pre>
         *
         * The following functions will available on the returned enum:
         * <ul>
         *   <li> each(callback: Function): *              - iterates over all constants, in ordinal order. Also possible
         *                                                   to do for (var constants in enumeration), but with unknown
         *                                                   order (and in modern browsers only).
         *   <li> lookup(id: String|Number|*, silent:*): * - lookup a given constant based on id, key, ordinal, or constant,
         *                                                   optionally fails with an error if not found. Second argument
         *                                                   can be a default constant to return on failure to look up, or
         *                                                   a (true) boolean value to suppress look up errors.
         *   <li> exists(id: String|Number|*): Boolean     - returns true if the specified constant exist, false otherwise.
         *   <li> __enumName: String                       - the specified type (name) of the enum.
         *   <li> __enumSize: Number                       - the number of constants in the enum.
         *   <li> __idField:  String                       - the id field that can also be used for lookup, if any.
         * </ul>
         *
         * In addition, each constant in the enum will be tagged with these properties:
         *
         * <ul>
         *   <li> is(constant:*): Boolean - function to check if the supplied argument corresponds to the constant in
         *                                  question, false otherwise.
         *   <li> _key: String            - its key to look up the ordinal; may be different from the "idField" to
         *                                  support additional look ups.
         *   <li> _ordinal: Number        - the ordinal number in the enum (index), corresponding to the define order
         *                                  <b>or</b> to the <tt>ordinal</tt> property defined on the constant already.
         *   <li> _enum: Enum             - the enum (singleton) the constant belongs to.
         *   <li> __enumName: String      - the specified type (name) of the enum.
         * </ul>
         *
         * If the constant defines an <tt>ordinal</tt> property as a number, all constants will be sorted accordingly.
         * The returned enum, however, will always used normalized ordinals, starting from zero. <p>
         *
         * In addition, defined enums can be statically tested and acquired like:
         * <pre>
         *    Enum.is(o:*): Boolean - returns true if the supplied object is an enum, false otherwise.
         *    Enum.get(o:*): Enum   - returns the internal enum based on the specified value.
         * </pre>
         *
         * @param {String}   type        The enum type as a string, e.g. "WidgetTypes"; cannot be falsey.
         * @param {String|*} idField     The field from each constant to be used on lookup, e.g. "id". If falsey, a
         *                               lookup will be made on the keys deduced from <tt>constants</tt> only when
         *                               a map, but must be supplied if <tt>constants</tt> is an array. If no specific
         *                               id field is to be used expect the enum keys, this can be left out (null) or
         *                               <tt>constants</tt> can be supplied as the second argument as well.
         * @param {[]|*}     [constants] The enum constants, where each key is expected to index the enum value <b>or</b> an
         *                               array indexed by its <tt>idField</tt> property; cannot be falsey. The map/array
         *                               is <b>not</b> modified by this function, but the individual constants will
         *                               be augmented as documented above.
         * @param {*}        [methods]   A set of static enum methods to be added to the returned enum type as well; can
         *                               be falsey. Can also be supplied as the third argument if no <tt>idField</tt> is
         *                               used. Functions named similar to enum functions will cause an error to be
         *                               raised.
         *
         * @return {*} A new enum as described; never falsey.
         */
        create(type, idField, constants, methods) {
            const enumeration = Object.create ? Object.create(null) : {}; // null: no prototype, and properties added below
            const index = {};
            const array = [];
            let sort  = false;
            let i;
            let s; // cached toString() representation
            let c;

            if (idField && typeof idField !== "string") {
                methods   = constants;
                constants = idField;
                idField   = null;
            }

            // Defines the optionally enumerable, but always immutable property "key" on "target" storing "value"...
            const defineProp = function(target, key, value, enumerable) {
                if (Object.defineProperty) {
                    Object.defineProperty(target, key, {
                        value,
                        enumerable: enumerable || false
                    });
                } else {
                    enumeration[key] = value;
                }
            };

            // Helper method to define a constant in this enum, regardless if supplied via an array or map...
            const defineConstant = function(constant, key) {
                // Any sealed constant will cause trouble, like a string constant...
                if (Object.isSealed && Object.isSealed(constant)) {
                    constant = {
                        value: constant,
                        toString() {
                            return this.value;
                        }
                    };
                    idField  = "value";
                }

                // A specified ordinal need just be a number, we will make sure it is valid after sorting below...
                if (typeof constant.ordinal === "number") {
                    constant._ordinal = constant.ordinal;
                    sort = true;
                } else {
                    constant._ordinal = array.length;
                }

                // Respect already non-native toString definition...
                if (!constant.toString.prototype) {
                    constant.toString = function() { // user-friendly toString for each constant if none is declared specifically
                        const s = `${constant._ordinal}: ${constant._key}`;
                        if (idField) {
                            return `${s}=${constant[idField]}`;
                        }
                        return s;
                    };
                }

                if (!key) {
                    throw new Error(`${type} enum does not define enum key: ${constant}`);
                }

                // For consistency, and to easier distinguish constants from methods, upper case all enum keys...
                key = key.toUpperCase();

                // Expose this constant under its key, and make it enumerable. We never look up based on "enumeration",
                // though, as it will also define methods like "all"...
                defineProp(enumeration, key, constant, true);
                index[key] = constant;

                // If we have an additional id, register it as well, but we must have it then...
                if (idField) {
                    const id = constant[idField];
                    if (!id) {
                        throw new Error(`${type} enum does not define '${idField}' field: ${constant}`);
                    }
                    index[id] = constant;
                }

                // Add to array so we can order by ordinal and iterate accordingly...
                array.push(constant);

                defineProp(constant, "_key", key);
                defineProp(constant, "_enum", enumeration);
                defineProp(constant, "__enumName", type); // for easy lookup

                // Enum types may define this already which we will respect...
                if (typeof constant.is === "undefined") {
                    defineProp(constant, "is", (c) => (c === constant) || (enumeration.lookup(c, true) === constant));
                }
            };

            // Array supplied, so we must have an "idField" to index the constants by <b>and</b> such keys available
            // for  constant...
            if (constants instanceof Array) {
                for (i = 0; i < constants.length; i++) {
                    c = constants[i];
                    defineConstant(c, c[idField]);
                }

            // Map (enum) supplied, where each map key is the constant name...
            } else {
                for (i in constants) {
                    defineConstant(constants[i], i);
                }
            }

            if (sort) {
                array.sort((a, b) => a._ordinal - b._ordinal);
            }

            // Make sure ordinals are sequential, starting with 0, and immutable henceforth...
            for (i = 0; i < array.length; i++) {
                c = array[i];
                delete c._ordinal;
                defineProp(c, "_ordinal", i);
            }

            defineProp(enumeration, "__enumName", type); // explicitly not called "__typeName" to avoid conflicts with Extend!
            defineProp(enumeration, "__enumSize", array.length); // do not call this "length" as that will perhaps make libs think it is array like...
            if (idField) {
                defineProp(enumeration, "__idField", idField);
            }

            UNIVERSE[type] = enumeration;

            // Add static methods...
            if (methods) {
                for (i in methods) {
                    defineProp(enumeration, i, methods[i]);
                }
            }

            /**
             * Returns true if the specified constant is part of this enumeration based on identity, false otherwise.
             *
             * @param  {*} constant The constant instance to test; can be falsey.
             * @return {Boolean} True if <tt>constant</tt> is part of this enumeration, false otherwise.
             * @see    #exists
             */
            defineProp(enumeration, "is", (constant) => array.indexOf(constant) !== -1);

            /**
             * Calls <tt>callback</tt>, optionally scoped according to <tt>scope</tt>, for each constant in this
             * enum in ordinal order. <p>
             *
             * If <tt>callback</tt> returns a defined value, the iteration will stop and the result will be returned;
             * otherwise null is returned.
             *
             * @param {Function} callback The function to be invoked with the current constant as the single argument,
             *                            optionally returning defined value to stop the iteration (and return said value
             *                            from this function); cannot be falsey.
             *
             * @return {*} The defined value returned by <tt>callback</tt>, if any; otherwise null.
             */
            defineProp(enumeration, "each", (callback, scope) => {
                let result;
                let i;

                for (i = 0; i < array.length; i++) {
                    result = callback.call(scope || callback, array[i]);
                    if (typeof result !== "undefined") {
                        return result;
                    }
                }
                return null;
            });

            /**
             * Similar to Array.map, produces a new array of values by mapping each enum value through a transformation
             * function (<tt>callback</tt>). The callback is passed the enum value.
             *
             * @param  {Function} callback The iteratee to be invoked with the current constant as the single argument.
             * @param  {*}        scope Optional callback scope.
             * @return {Array}    The mapped array.
             */
            defineProp(enumeration, "map", (callback, scope) => {
                const result = [];

                enumeration.each((value) => {
                    result.push(callback.call(scope || callback, value));
                }, scope);

                return result;
            });

            /**
             * Returns a new array of all constants in ordinal order. <p>
             *
             * Modifying the returned array will not affect this enum (but do not change the individual constants).
             *
             * @param  {Function} [sort] Optional sort function to sort constants before returned; can be falsey, in
             *                           which case ordinal order is used.
             *
             * @return {[]} A new array of all constants in ordinal order; never falsey.
             */
            defineProp(enumeration, "all", (sort) => {
                const clone = array.slice();
                if (sort) {
                    clone.sort(sort);
                }
                return clone;
            });

            /**
             * Returns the constant with specified key, alternative id, ordinal, or equal to the specified constant
             * if it exist in this enumeration, undefined or raises an error otherwise, depending on the
             * value of <tt>silent</tt>. <p>
             *
             * If <tt>id</tt> is a string, it will be tried looked up as a constant key in a case sensitive manner.
             *
             * @param  {String|Number|*} id      The key, alternative id, ordinal, or constant to lookup; a falsey
             *                                   value will always return false.
             * @param  {Boolean|*}      [silent] True to return undefined if no found, false to raise an error
             *                                   if not found (default). Alternatively a default constant to
             *                                   return if the lookup fails as a key, alternative id, ordinal,
             *                                   or constant to use (can raise errors!)
             *
             * @return {*} The specified constant from this enum; can be null if <tt>silent</tt> is true.
             */
            defineProp(enumeration, "lookup", (id, silent) => {
                const t = typeof id;
                let constant;

                // eslint-disable-next-line eqeqeq
                if (id != null) {
                    if (t === "string") { // id
                        constant = index[id] || index[id.toUpperCase()]; // first 'ENUM KEY', secondly some additional 'id', or try UPPERCASE id as last resort...
                    } else if (t === "number") { // ordinal
                        constant = array[id];
                    } else if (id.__enumName === type && idField) { // constant
                        constant = index[id[idField]];
                    }
                }

                if (constant) {
                    return constant;
                } else if (silent) {
                    return typeof silent === "boolean" ? null : enumeration.lookup(silent); // may raise an error!
                }
                throw new Error(`${type} not found: ${id}`);
            });

            /**
             * Returns true if the specified id, ordinal, or constant exist in this enumeration,
             * false otherwise.
             *
             * @param  {String|Number|*} id The id, key, ordinal, or constant to lookup; a falsey value will
             *                              always return false.
             *
             * @return {Boolean} True if such a constant exist, false otherwise.
             */
            defineProp(enumeration, "exists", (id) => !!enumeration.lookup(id, true));

            /**
             * Enum specific mixin function that takes every (key,value) pair from <tt>values</tt> and mixes it
             * into the existing value in this enumeration. <p>
             *
             * If a key does not exist, an error will be raised. If private constant fields like <tt>_oridinal</tt>
             * are tried overridden, an error will be raised.
             *
             * @param  {*}         values       The (key,value) pairs to mixin on existing constants; can be falsey. Does not support
             *                                  arrays. Any value not an object will be ignored.
             * @param  {Function} [initializer] Optional initializer, fn(constant), to be invoked on each constant once
             *                                  relevant values have been mixed in, for example to generate defaults.
             * @return {Enum} This enumeration; never falsey.
             */
            defineProp(enumeration, "mixin", (values, initializer) => { // not the same as Extend.mixin, different enum semantics!
                if (values) {
                    let key;
                    let value;
                    let constant;

                    for (key in values) {
                        // Will raise an error if no such constant...
                        constant = enumeration.lookup(key);
                        value = values[key];
                        // Must be an object for us to iterate over...
                        if (typeof value === "object") {
                            for (key in value) {
                                constant[key] = value[key];
                            }
                        }
                        // Call supplied initializer to allow easy defaults across all constants...
                        if (initializer) {
                            initializer(constant);
                        }
                    }
                }
                return enumeration;
            });

            /**
             * Returns the string representation of this enum.
             *
             * @return {String} The string representation; never falsey.
             */
            defineProp(enumeration, "toString", () => {
                if (!s) {
                    s = "";
                    enumeration.each((constant) => {
                        if (s) {
                            s += "; ";
                        }
                        s += constant;
                    });
                    s = `${type}[${s}]`;
                }
                return s;
            });

            // If possible, prevent more stuff to be added to the enumeration...
            return Object.freeze ? Object.freeze(enumeration) : enumeration;
        },

        /**
         * Returns true if the supplied object is an enum, false otherwise.
         *
         * @param  {*} o The object to test; can be falsey, in which case false is returned.
         * @return {Boolean} True if <tt>o</tt> is an enum, false otherwise.
         */
        is(o) {
            return o && o.__enumSize > 0;
        },

        /**
         * Returns the enum associated with the supplied object, either a constant or an enum (or something
         * else entirely).
         *
         * @param  {*} o The object to find the enum for; can be falsey, in which case falsey is returned.
         * @return {Enum} The found enum, if any; can be falsey.
         */
        get(o) {
            return o && UNIVERSE[o.__enumName];
        }

    };

    return Enum;
});



// WEBPACK FOOTER //
// ./src/shared/util/Enum.js